// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/** The label a content code reads in, from the record's own labels; the code itself where the record names none. */
export function periodicReportContentLabel(block, code, vietnamese, significantChangesLabel) {
    const declared = block.contents.find((c) => c.code === code);
    if (declared)
        return vietnamese ? declared.label.vi : declared.label.en;
    return code === 'significant_changes' ? significantChangesLabel : code;
}
function gapText(gap, block, copy, vietnamese) {
    const n = String(gap.count ?? 0);
    switch (gap.kind) {
        case 'record_not_installed':
            return copy.gapRecordNotInstalled ?? copy.nextDueNotDeclared;
        case 'unclassified_systems':
            return copy.gapUnclassified.replace('{n}', n);
        case 'significance_not_recorded':
            return copy.gapSignificance.replace('{n}', n);
        case 'operation_date_not_recorded':
            return copy.gapOperationDate.replace('{n}', n);
        case 'content_unreadable':
            return copy.gapUnreadable.replace('{content}', periodicReportContentLabel(block, gap.content ?? '', vietnamese, copy.sigHeading));
        default: {
            // Every kind is handled above; a kind added to `AI_PERIODIC_REPORT_GAP_KINDS` fails to compile here.
            const unreachable = gap.kind;
            return unreachable;
        }
    }
}
/** The lines for a block read for `window`. `day` formats a `YYYY-MM-DD` for the reader. */
export function periodicReportLines(block, window, copy, options) {
    const from = options.day(window.from);
    const to = options.day(window.to);
    const gaps = block.gaps.map((g) => gapText(g, block, copy, options.vietnamese));
    const status = block.partial
        ? copy.statusPartial
            .replace('{gaps}', gaps.join('; '))
            .replace('{from}', from)
            .replace('{to}', to)
        : copy.statusComplete.replace('{from}', from).replace('{to}', to);
    const due = block.next_due;
    let nextDue;
    if (due.reading === 'cadence_not_declared')
        nextDue = copy.nextDueNotDeclared;
    else if (due.reading === 'framework_undated')
        nextDue = copy.nextDueUndated ?? copy.nextDueNotDeclared;
    else if (due.reading === 'no_system_in_scope')
        nextDue = copy.nextDueNoScope;
    else if (due.date === null || due.anchor_date === null)
        nextDue = copy.nextDueUnknown;
    else {
        const anchor = (due.anchor_kind === 'last_export' ? copy.anchorLastExport : copy.anchorFirstApplies).replace('{date}', options.day(due.anchor_date));
        nextDue = copy.nextDue.replace('{date}', options.day(due.date)).replace('{anchor}', anchor);
        // Gate E12.5 CMP-P2-1 — a day dated through another provision is the platform's reading, and the line says so in
        // the declaration's own words.
        const reading = due.anchor_kind === 'first_applies' ? (due.anchor_reading ?? null) : null;
        if (reading !== null)
            nextDue = `${nextDue}. ${options.vietnamese ? reading.vi : reading.en}`;
    }
    return {
        status,
        gaps,
        windowNote: block.window_longer_than_interval ? copy.windowLonger : null,
        nextDue,
        autonomyNote: due.autonomy_not_recorded > 0
            ? copy.autonomyNotRecorded.replace('{n}', String(due.autonomy_not_recorded))
            : null,
    };
}
//# sourceMappingURL=ai-periodic-report-lines.js.map